import React, { useMemo } from 'react';
import { TPaginationProps } from './types/pagination.types';
import useMediaQuery from '../../../lib/custom-hooks/useMediaQuery';
import { BreakpointsInPx } from '../../../constants/breakpoints.constants';
import Icon from '../icon';
import { iconSize } from '../../../constants/icon.constants';

const Pagination = ({ currentPage, goToPage, pages }: TPaginationProps): JSX.Element => {
  const EMPTY_BUTTON_LABEL = '...';
  const FIRST_PAGE = 1;
  const pagesArr = useMemo(() => new Array(pages).fill(null).map((_, i: number) => i + 1), [pages]);
  const isMobile = useMediaQuery(`(max-width: ${BreakpointsInPx.md})`);

  const handlePageClick = (page: number): void => {
    if (page === currentPage) {
      return;
    }
    goToPage(page);
  };

  const filterMobileInRangePage = (pageToCheck: number): boolean => {
    if (currentPage + 5 >= pages) {
      return pageToCheck <= pages && pageToCheck > pages - 6;
    } else {
      return pageToCheck >= currentPage && pageToCheck < currentPage + 6;
    }
  };

  const filterDesktopInRangePage = (pageToCheck: number): boolean => {
    const adjacentPageToShow = 2;

    if (pages < 10) return true;

    if (pageToCheck === FIRST_PAGE || pageToCheck === pages) return true;

    if (currentPage === pageToCheck) return true;

    if (currentPage >= 1 && currentPage <= 7) return pageToCheck >= 1 && pageToCheck <= 7;

    if (currentPage >= pages - 6 && currentPage <= pages) return pageToCheck >= pages - 6 && pageToCheck <= pages;

    if (currentPage < pageToCheck && currentPage + adjacentPageToShow >= pageToCheck) return true;

    if (currentPage > pageToCheck && currentPage - adjacentPageToShow <= pageToCheck) return true;

    return false;
  };

  const filterInRangePage = (pageToCheck: number): boolean => {
    if (isMobile) {
      return filterMobileInRangePage(pageToCheck);
    } else {
      return filterDesktopInRangePage(pageToCheck);
    }
  };

  const addEmptyButton = (acc: (number | string)[], curr: number): (number | string)[] => {
    if (isMobile) {
      return [...acc, curr];
    }

    if (curr === FIRST_PAGE) {
      return [...acc, curr];
    }

    if (pages < 10) {
      return [...acc, curr];
    }

    const lastEl = acc[acc.length - 1];
    if (lastEl === FIRST_PAGE && curr - 1 > FIRST_PAGE) {
      acc.push(EMPTY_BUTTON_LABEL);
    }
    if (curr === pages && lastEl !== pages - 1) {
      acc.push(EMPTY_BUTTON_LABEL);
    }
    return [...acc, curr];
  };

  return (
    <>
      {pages > 1 && (
        <section className="pagination">
          <div className="pagination__container">
            <button
              className={`pagination__arrow pagination__arrow--${currentPage === 1 ? 'first' : ''}`}
              onClick={(): void => handlePageClick(currentPage - 1)}
            >
              {pages <= 1 ? (
                ''
              ) : (
                <Icon iconName="arrow-chevron-left" size={iconSize.dsk} color={'primary'} extraClassName="pagination__arrow--lefticon" />
              )}
            </button>
            {pagesArr
              .filter(filterInRangePage)
              .reduce(addEmptyButton, [])
              .map((page: number | string, i: number) => {
                const currentPageBtnClassName = page === currentPage ? ' pagination__button--active' : '';
                return typeof page === 'string' ? (
                  <button className="pagination__button pagination__button--disabled" key={`page-${i}`} disabled>
                    {page}
                  </button>
                ) : (
                  <button
                    className={'pagination__button' + currentPageBtnClassName}
                    key={`page-${i}`}
                    onClick={(): void => handlePageClick(page)}
                  >
                    {page}
                  </button>
                );
              })}
            <button
              className={`pagination__arrow pagination__arrow--${pagesArr.length === currentPage ? 'last' : ''}`}
              onClick={(): void => handlePageClick(currentPage + 1)}
            >
              {pages <= 1 ? (
                ''
              ) : (
                <Icon iconName="arrow-chevron-right" size={iconSize.dsk} color={'primary'} extraClassName="pagination__arrow--righticon" />
              )}
            </button>
          </div>
        </section>
      )}
    </>
  );
};

export default Pagination;
